import { z } from 'zod';
import { esquemaId, esquemaMotivo } from './comunes.js';
import { esquemaInstante } from './cita.js';
import { instanteDeHoraLocal, sumarDias, zonaHorariaValida } from '../tiempo.js';
import { DEFINICION_DE_OBSERVACION } from '../catalogos/observaciones.js';
/**
 * Planes de atencion versionados (F4.5, PLAN-01, plan maestro §8.3 y §8.4).
 *
 * - El plan tiene la maquina de estados de §8.3. Su BORRADOR se edita con
 *   concurrencia optimista: dos profesionales que editan a la vez reciben 409,
 *   nunca se pisan (AC-16).
 * - Publicar congela una REVISION con su fecha de entrada en vigor y su zona
 *   horaria. La vigente es la ultima publicada cuya fecha ya llego: nunca dos a
 *   la vez. La anterior se conserva intacta (AC-06).
 * - Las actividades generan OCURRENCIAS con identificador unico (actividad +
 *   fecha y hora local). Una revision nueva sustituye solo las futuras sin
 *   completar; lo completado no se toca (AC-24).
 */
export const ESTADOS_DE_PLAN = [
    'borrador',
    'revision',
    'programado',
    'activo',
    'pausado',
    'completado',
    'sustituido',
    'cancelado',
];
/**
 * Transiciones manuales permitidas (§8.3). Publicar (revision o activo →
 * programado o activo) y la entrada en vigor (programado → activo) no estan
 * aqui: tienen su propio camino.
 */
export const TRANSICIONES_DE_PLAN = {
    borrador: ['revision'],
    revision: ['borrador'],
    programado: ['cancelado'],
    activo: ['pausado', 'completado', 'sustituido'],
    pausado: ['activo', 'cancelado'],
    completado: [],
    sustituido: [],
    cancelado: [],
};
/** Las que cierran el plan y exigen motivo. */
export const TRANSICIONES_CON_MOTIVO = [
    'pausado',
    'completado',
    'sustituido',
    'cancelado',
];
export const TIPOS_DE_ACTIVIDAD = ['tarea', 'medicion', 'recordatorio'];
const hora = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Hora HH:MM');
const fecha = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha AAAA-MM-DD');
/** Recurrencia LIMITADA (§8.4): a lo mas un año y 400 ocurrencias. */
export const MAXIMO_DE_OCURRENCIAS = 400;
export const MAXIMO_DE_DIAS = 366;
export const esquemaProgramacion = z.discriminatedUnion('tipo', [
    z.object({ tipo: z.literal('unica'), fecha, hora }).strict(),
    z
        .object({
        tipo: z.literal('semanal'),
        /** 0 = domingo … 6 = sabado, en el calendario del consultorio. */
        dias: z.array(z.number().int().min(0).max(6)).min(1).max(7),
        hora,
        desde: fecha,
        hasta: fecha,
    })
        .strict(),
]);
export const esquemaActividad = z
    .object({
    /** Identificador estable dentro del plan, ASCII: con el se reconcilian las revisiones. */
    id: z.string().regex(/^[a-z0-9-]{2,40}$/, 'Identificador en minúsculas, números o guiones'),
    titulo: z.string().trim().min(2).max(160),
    tipo: z.enum(TIPOS_DE_ACTIVIDAD),
    programacion: esquemaProgramacion,
    duracionMinutos: z
        .number()
        .int()
        .min(1)
        .max(24 * 60)
        .optional(),
})
    .strict()
    .superRefine((actividad, ctx) => {
    const p = actividad.programacion;
    if (p.tipo !== 'semanal')
        return;
    if (p.desde > p.hasta) {
        ctx.addIssue({
            code: 'custom',
            message: 'La fecha final va después de la inicial',
            path: ['programacion', 'hasta'],
        });
    }
    const dias = (Date.parse(`${p.hasta}T00:00:00Z`) - Date.parse(`${p.desde}T00:00:00Z`)) / 86_400_000;
    if (dias > MAXIMO_DE_DIAS) {
        ctx.addIssue({
            code: 'custom',
            message: `La recurrencia dura a lo más ${MAXIMO_DE_DIAS} días`,
            path: ['programacion', 'hasta'],
        });
    }
});
/**
 * Alimentacion (F4.6, NUT-01, plan §7.3): estructura nutricional DENTRO del
 * plan, con su mismo versionado y entrada en vigor. Porciones e indicaciones
 * que escribe el profesional: sin catalogo de alimentos ni calculo nutricional,
 * sin metas ni dietas automaticas (S-09).
 */
export const LIMITES_DE_ALIMENTACION = {
    sustituciones: 10,
    alimentos: 20,
    tiempos: 10,
    bloques: 14,
};
export const esquemaOpcionDeAlimento = z
    .object({
    descripcion: z.string().trim().min(2).max(200),
    porcion: z.string().trim().max(120).default(''),
    /** Sustituciones autorizadas por el profesional, en texto. */
    sustituciones: z
        .array(z.string().trim().min(2).max(200))
        .max(LIMITES_DE_ALIMENTACION.sustituciones)
        .default([]),
})
    .strict();
export const esquemaTiempoDeComida = z
    .object({
    nombre: z.string().trim().min(2).max(60),
    hora: hora.optional(),
    opciones: z.array(esquemaOpcionDeAlimento).max(LIMITES_DE_ALIMENTACION.alimentos).default([]),
    notas: z.string().trim().max(500).default(''),
})
    .strict();
export const esquemaDiaDeAlimentacion = z
    .object({
    etiqueta: z.string().trim().min(2).max(60),
    /** 0 = domingo … 6 = sabado. Vacio: todos los dias. */
    diasDeSemana: z.array(z.number().int().min(0).max(6)).max(7).default([]),
    tiempos: z.array(esquemaTiempoDeComida).min(1).max(LIMITES_DE_ALIMENTACION.tiempos),
})
    .strict();
export const esquemaAlimentacion = z
    .object({
    dias: z.array(esquemaDiaDeAlimentacion).min(1).max(LIMITES_DE_ALIMENTACION.bloques),
    restricciones: z.array(z.string().trim().min(2).max(200)).max(20).default([]),
    preferencias: z.array(z.string().trim().min(2).max(200)).max(20).default([]),
})
    .strict();
/**
 * Lo que toca comer una fecha (AAAA-MM-DD del consultorio): los bloques cuyo
 * dia de semana coincide; si ninguno lo nombra, los de «todos los dias».
 */
export function alimentacionDelDia(alimentacion, fecha) {
    const [a, m, d] = fecha.split('-').map(Number);
    const diaDeSemana = new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay();
    const especificos = alimentacion.dias.filter((dia) => dia.diasDeSemana.includes(diaDeSemana));
    return especificos.length > 0
        ? especificos
        : alimentacion.dias.filter((dia) => dia.diasDeSemana.length === 0);
}
export const esquemaCrearPlantillaDeAlimentacion = z
    .object({ nombre: z.string().trim().min(2).max(120), alimentacion: esquemaAlimentacion })
    .strict();
export const esquemaContenidoDePlan = z
    .object({
    /** Meta individual acordada por el profesional; se publica con la revision. */
    metaPesoKg: z
        .number()
        .finite()
        .min(DEFINICION_DE_OBSERVACION.peso.rango.min)
        .max(DEFINICION_DE_OBSERVACION.peso.rango.max)
        .optional(),
    objetivos: z.array(z.string().trim().min(2).max(300)).max(20).default([]),
    indicaciones: z.string().trim().max(10_000).default(''),
    actividades: z
        .array(esquemaActividad)
        .max(50)
        .default([])
        .refine((a) => new Set(a.map((x) => x.id)).size === a.length, {
        message: 'Cada actividad necesita un identificador distinto',
    }),
    /** Solo en planes de nutricion (exige el modulo). */
    alimentacion: esquemaAlimentacion.optional(),
})
    .strict();
const zona = z.string().refine(zonaHorariaValida, 'Zona horaria inválida');
export const esquemaCrearPlan = z
    .object({
    pacienteId: esquemaId,
    titulo: z.string().trim().min(2).max(160),
    disciplina: z.string().trim().min(2).max(80),
    /** Por defecto, la del consultorio. Toda la programacion se interpreta en ella. */
    zonaHoraria: zona.optional(),
    contenido: esquemaContenidoDePlan.default({}),
})
    .strict();
export const esquemaGuardarBorradorDePlan = z
    .object({
    versionEsperada: z.number().int().min(0),
    titulo: z.string().trim().min(2).max(160).optional(),
    zonaHoraria: zona.optional(),
    contenido: esquemaContenidoDePlan,
})
    .strict();
export const esquemaTransicionDePlan = z
    .object({
    versionEsperada: z.number().int().min(0),
    estado: z.enum(ESTADOS_DE_PLAN),
    motivo: esquemaMotivo.optional(),
})
    .strict();
export const esquemaPublicarPlan = z
    .object({
    versionEsperada: z.number().int().min(0),
    /** Desde cuando vale la revision. Sin el, ahora. */
    entraEnVigor: esquemaInstante.optional(),
})
    .strict();
export const ESTADOS_DE_OCURRENCIA = [
    'pendiente',
    'completada',
    'cancelada',
    'sustituida',
];
/**
 * Las ocurrencias de una actividad, en la zona del plan. La clave identifica
 * la ocurrencia: actividad y fecha y hora LOCALES. Generar dos veces da lo
 * mismo, y el indice unico impide duplicar.
 */
export function ocurrenciasDe(actividad, zonaHoraria) {
    const p = actividad.programacion;
    const salida = [];
    const agregar = (dia, hhmm) => salida.push({
        clave: `${actividad.id}@${dia}T${hhmm}`,
        inicio: instanteDeHoraLocal(dia, hhmm, zonaHoraria),
    });
    if (p.tipo === 'unica') {
        agregar(p.fecha, p.hora);
        return salida;
    }
    let dia = p.desde;
    for (let i = 0; i < MAXIMO_DE_DIAS && dia <= p.hasta; i += 1) {
        const [a, m, d] = dia.split('-').map(Number);
        const diaDeSemana = new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay();
        if (p.dias.includes(diaDeSemana))
            agregar(dia, p.hora);
        if (salida.length >= MAXIMO_DE_OCURRENCIAS)
            break;
        dia = sumarDias(dia, 1);
    }
    return salida;
}
//# sourceMappingURL=plan.js.map